<template>
    <div class="h-full w-full max-w-full">
        <div class="flex h-[calc(100vh-20px)]">
            <!-- EventMenu - Main component for event management -->
            <EventMenu 
                data-tour="schedule-menu"
                class="hidden lg:block"
                @add-event="handleAddEvent"
                @filter-change="handleFilterChange"
                :categories="categories"
            />

            <!-- CalendarComponent - Main component for calendar display -->
            <div class="flex-1 bg-white dark:bg-gray-800 rounded-lg min-w-0 overflow-hidden flex flex-col">
                <!-- Offline Notice Banner -->
                <div v-if="isOfflineNoticeVisible" class="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/50 px-4 py-1.5 flex items-center justify-between text-xs text-amber-800 dark:text-amber-200">
                    <div class="flex items-center gap-2">
                        <CloudArrowDownIcon class="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span>{{ $t('schedule.offlineNotice') }}</span>
                    </div>
                </div>

                <!-- CalendarHeader component -->
                <div data-tour="schedule-header" class="w-full">
                    <CalendarHeader
                        :current-date="currentDate"
                        :current-view="currentView"
                        :is-loading="isLoading"
                        :categories="categories"
                        :is-downloading-schedule="isDownloadingSchedule"
                        :is-schedule-downloaded="isScheduleDownloaded"
                        @date-change="handleDateChange"
                        @view-change="handleViewChange"
                        @add-event="handleAddEvent"
                        @filter-change="handleFilterChange"
                        @download-schedule="handleDownloadSchedule"
                        @start-tour="() => startScheduleTour(true)"
                    />
                </div>
                
                <!-- CalendarGrid component -->
                <div data-tour="schedule-grid" class="h-[calc(100%-80px)] max-h-[calc(100%-80px)] min-w-0 overflow-hidden">
                    <CalendarGrid
                        :current-date="seeMoreDate || currentDate"
                        :view="currentView"
                        :events="filteredEvents"
                        :categories="categories"
                        :show-specific-date="!!seeMoreDate"
                        @event-select="handleEventSelect"
                        @add-event="handleAddEvent"
                        @see-more="handleSeeMore"
                        @back-to-calendar="handleBackToCalendar"
                    />
                </div>
            </div>

            <!-- EventDetails - Side panel for event details -->
            <Modal ref="eventDetailModal" :title="$t('schedule.eventDetails')">
                <div v-if="selectedEvent" class="bg-white dark:bg-gray-800">
                    <EventDetails
                        :event="selectedEvent"
                        :categories="categories"
                        @edit-event="handleEditEvent"
                        @delete-event="handleDeleteEvent"
                        @open-playlist="handleOpenPlaylist"
                    />
                </div>
            </Modal>
            <Modal ref="eventFormModal" :title="(editingEvent?._id || editingEvent?.id) ? $t('schedule.editEvent') : $t('schedule.addEvent')" size="large">
                <EventForm
                    :show="showEventForm"
                    :event="editingEvent"
                    @close="closeEventForm"
                    @save="handleSaveEvent"
                    :categories="categories"
                />
            </Modal>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import appStore from "@/store/app.store";
import { appEvents } from '@/store/app.events';
import ScheduleService from "@/service/schedule.service.js";
import offlineScheduleService from "@/service/offlineSchedule.service.js";
import { CloudArrowDownIcon } from '@heroicons/vue/24/outline';
import {
    EventMenu,
    CalendarHeader,
    CalendarGrid,
    EventDetails,
    EventForm
} from "../../components/schedule"
import toastStore from '@/store/toast.store'
import { useI18n } from 'vue-i18n'
import { useTour } from '@/composables/useTour'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { startScheduleTour } = useTour()

// Set current view for app store
appStore.state.currentView = "Schedule"

// Offline schedule state
const isDownloadingSchedule = ref(false)
const isScheduleDownloaded = ref(false)
const isOfflineNoticeVisible = ref(false)

// Reactive data
const currentDate = ref(new Date())
const currentView = ref(window.innerWidth < 1024 ? 'List' : 'Month')
const isLoading = ref(false)
const selectedEvent = ref(null)
const showEventForm = ref(false)
const editingEvent = ref(null)
const eventFilters = ref({
    'Personal': true,
    'Team Rehearsal': true,
    'Live Performance': true,
    'General': true
})
const eventDetailModal = ref(null)
const eventFormModal = ref(null);

const events = ref([])
const seeMoreDate = ref(null)
const seeMoreEvents = ref([])
const categories = ref([])

// Computed filtered events
const filteredEvents = computed(() => {
    if (seeMoreDate.value && currentView.value === 'List') {
        return seeMoreEvents.value.filter(event => eventFilters.value[event.category])
    }
    // event.category is now the category name string (from DTO transformation)
    return events.value.filter(event => eventFilters.value[event.category])
})

// Helper function to round time to nearest 00 or 30 minutes
const roundTimeToNearestHalfHour = (date) => {
    const newDate = new Date(date.getTime())
    const minutes = newDate.getMinutes()
    
    if (minutes > 30) {
        // If minutes > 30, set to next hour at 00 minutes
        newDate.setHours(newDate.getHours() + 1, 0, 0, 0)
    } else {
        // If minutes <= 30, set to current hour at 30 minutes
        newDate.setMinutes(30, 0, 0)
    }
    
    return newDate
}

// Event handlers
const handleDateChange = (newDate) => {
    currentDate.value = newDate
    fetchEvents()
}

const handleViewChange = (newView) => {
    currentView.value = newView
    // Reset see-more state when changing views
    if (newView !== 'List') {
        seeMoreDate.value = null
        seeMoreEvents.value = []
    }
}

const handleSeeMore = (data) => {
    seeMoreDate.value = data.date
    seeMoreEvents.value = data.events
    currentView.value = 'List'
}

const handleBackToCalendar = () => {
    seeMoreDate.value = null
    seeMoreEvents.value = []
    currentView.value = 'Month'
}

const handleEventSelect = (event) => {
    selectedEvent.value = event
    eventDetailModal.value.open()
}

const handleAddEvent = (eventData) => {
    const handleEventObj ={...eventData}
    editingEvent.value = null
    
    // Get the base time (either from eventData.time or current time + 1 hour on the selected date)
    let baseTime;
    if (handleEventObj.time) {
        baseTime = new Date(handleEventObj.time.getTime());
    } else {
        baseTime = handleEventObj.date ? new Date(handleEventObj.date.getTime()) : new Date();
        baseTime.setHours(new Date().getHours() + 1);
    }
    
    // Round the time to nearest 00 or 30 minutes
    const roundedTime = roundTimeToNearestHalfHour(baseTime)
    
    // Set end time to 2 hours after the rounded start time
    const endTime = new Date(roundedTime.getTime())
    endTime.setHours(endTime.getHours() + 2)
    
    editingEvent.value = {
        ...handleEventObj,
        startTime: roundedTime,
        endTime: endTime,
        categoryId: handleEventObj.categoryId || (categories.value.length > 0 ? categories.value[0].id : ''),
        description: handleEventObj.description || '',
        isOwner: true
    }
    showEventForm.value = true
    eventFormModal.value.open();
}

const handleEditEvent = (event) => {
    if (!event.isOwner) {
        return
    }
    editingEvent.value = event
    showEventForm.value = true
    eventDetailModal.value.close();
    eventFormModal.value.open();
}

const handleDeleteEvent = async (event) => {
    if (!event.isOwner) {
        return
    }
    const id = event._id
    try{
        const result = await ScheduleService.remove(id)
        console.log('Delete result:', result)
        if (result && result.error) {
            toastStore.dispatch('addMessage', {
                message: result.message || 'Failed to delete event',
                icon: 'error',
                ttl: 3000
            })
            return
        }
        await fetchEvents()
        toastStore.dispatch('addMessage', {
            message: t('schedule.eventDeleted', 'Evento excluído'),
            icon: 'success',
            ttl: 3000
        });
    } catch (error) {
        console.error('Error deleting event:', error)
        toastStore.dispatch('addMessage', {
            message: t('schedule.notification.deleted', 'Erro ao excluir evento'),
            icon: 'error',
            ttl: 3000
        })
    } finally {
        eventDetailModal.value.close();
    }
}

const closeEventForm = () => {
    showEventForm.value = false
    editingEvent.value = null;
    eventFormModal.value.close()
}

const handleSaveEvent = async (eventData) => {
    // Normalize payload
    const payload = { ...eventData }
    if (payload.date instanceof Date) {
        payload.date = payload.date.toISOString()
    }
    if (payload.startTime instanceof Date) payload.startTime = payload.startTime.toISOString()
    if (payload.endTime instanceof Date) payload.endTime = payload.endTime.toISOString()

    const eventId = editingEvent.value?._id || editingEvent.value?.id
    if (eventId && editingEvent.value.isOwner) {
        await ScheduleService.update(eventId, payload)
        toastStore.dispatch('addMessage', {
            message: t('schedule.eventUpdated', 'Evento atualizado'),
            icon: 'success',
            ttl: 3000
        });
        await fetchEvents()
    } else {
        await ScheduleService.create(payload)
        toastStore.dispatch('addMessage', {
            message: t('schedule.eventCreated', 'Evento criado'),
            icon: 'success',
            ttl: 3000
        });
        await fetchEvents();
    }
}

const handleFilterChange = (filters) => {
    eventFilters.value = filters.reduce((acc, filter) => {
        acc[filter.name] = filter.selected
        return acc
    }, {})
}

const getCurrentViewRange = () => {
    const date = currentDate.value
    const start = new Date(date.getFullYear(), date.getMonth(), 1)
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999)
    return { from: start.toISOString(), to: end.toISOString() }
}

const mapEventsToUI = (eventList) => {
    return eventList.map(e => {
        const start = e.startTime ? new Date(e.startTime) : null
        const end = e.endTime ? new Date(e.endTime) : null
        let eventDate = null
        if (start) {
            eventDate = new Date(start.getFullYear(), start.getMonth(), start.getDate())
        } else if (e.date) {
            if (typeof e.date === 'string') {
                const [y, m, d] = e.date.slice(0, 10).split('-').map(Number)
                eventDate = new Date(y, m - 1, d)
            } else {
                eventDate = new Date(e.date)
            }
        }
        return {
            ...e,
            date: eventDate,
            startTime: start,
            endTime: end,
        }
    })
}

const checkOfflineStatus = async () => {
    const meta = await offlineScheduleService.getScheduleMeta()
    isScheduleDownloaded.value = !!meta
    isOfflineNoticeVisible.value = !navigator.onLine && !!meta
}

const fetchEvents = async () => {
    isLoading.value = true
    try {
        if (!navigator.onLine) {
            const cached = await offlineScheduleService.getCachedEvents()
            events.value = mapEventsToUI(cached)
            isOfflineNoticeVisible.value = true
            return
        }

        const params = getCurrentViewRange()
        const data = await ScheduleService.list(params)
        const eventList = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : [])
        events.value = mapEventsToUI(eventList)
        isOfflineNoticeVisible.value = false
    } catch (error) {
        console.warn('[ScheduleView] Failed to fetch events from network, checking offline cache:', error)
        const cached = await offlineScheduleService.getCachedEvents()
        if (cached && cached.length > 0) {
            events.value = mapEventsToUI(cached)
            isOfflineNoticeVisible.value = true
        }
    } finally {
        isLoading.value = false
        await checkOfflineStatus()
    }
}

const fetchCategories = async () => {
    try {
        let data = []
        if (navigator.onLine) {
            data = await ScheduleService.getCategories()
        } else {
            data = await offlineScheduleService.getCachedCategories()
        }
        if (!data || data.length === 0) {
            data = await offlineScheduleService.getCachedCategories()
        }
        categories.value = data.map((c) => ({ ...c, selected: true }))
        eventFilters.value = data.reduce((acc, c) => {
            acc[c.name] = true
            return acc
        }, {})
    } catch (e) {
        const cached = await offlineScheduleService.getCachedCategories()
        if (cached && cached.length > 0) {
            categories.value = cached.map((c) => ({ ...c, selected: true }))
            eventFilters.value = cached.reduce((acc, c) => {
                acc[c.name] = true
                return acc
            }, {})
        }
    }
}

const handleDownloadSchedule = async () => {
    if (!navigator.onLine) {
        toastStore.dispatch('addMessage', {
            message: t('toast.offlineWriteError') || 'Você está offline. Conecte-se à internet para baixar a agenda.',
            icon: 'warning',
            ttl: 3000
        })
        return
    }

    isDownloadingSchedule.value = true
    try {
        const result = await offlineScheduleService.downloadUpcomingSchedule({
            daysAhead: 35,
            downloadLinkedPlaylists: true
        })

        if (result && result.success) {
            isScheduleDownloaded.value = true
            toastStore.dispatch('addMessage', {
                message: t('schedule.scheduleDownloaded', { count: result.count }) || `Agenda offline atualizada com sucesso (${result.count} eventos)`,
                icon: 'success',
                ttl: 4000
            })
        }
    } catch (err) {
        console.error('Failed to download schedule offline:', err)
        toastStore.dispatch('addMessage', {
            message: 'Erro ao baixar agenda para uso offline.',
            icon: 'error',
            ttl: 4000
        })
    } finally {
        isDownloadingSchedule.value = false
    }
}

const handleOpenPlaylist = (playlist) => {
    const id = playlist.id || playlist._id
    if (id) {
        eventDetailModal.value?.close()
        const backdrops = document.querySelectorAll('[modal-backdrop]')
        backdrops.forEach(b => b.remove())
        document.body.classList.remove('overflow-hidden')
        router.push(`/playlist/${id}/song`)
    }
}

const checkDirectEventOpen = async () => {
    const eventId = route.query?.eventId
    if (!eventId) return
    let target = events.value.find(e => String(e.id || e._id) === String(eventId))
    if (!target) {
        try {
            const res = await ScheduleService.get(eventId)
            if (res && !res.error) {
                const rawEvent = res.data || res
                const mapped = mapEventsToUI([rawEvent])
                target = mapped[0]
            }
        } catch (err) {
            console.warn('[ScheduleView] Error fetching direct event by ID:', err)
        }
    }
    if (target) {
        if (target.startTime || target.date) {
            currentDate.value = new Date(target.startTime || target.date)
            await fetchEvents()
            const foundInLoaded = events.value.find(e => String(e.id || e._id) === String(eventId))
            if (foundInLoaded) {
                target = foundInLoaded
            }
        }
        await nextTick()
        handleEventSelect(target)
    }
}

onMounted(async () => {
    await checkOfflineStatus()
    await Promise.all([
        fetchCategories(),
        fetchEvents()
    ])
    await checkDirectEventOpen()
    appEvents.on('schedule:refresh', fetchEvents)
    appEvents.on('offline:schedule-downloaded', checkOfflineStatus)
    appEvents.on('offline:schedule-cleared', checkOfflineStatus)
    window.addEventListener('online', fetchEvents)
    window.addEventListener('offline', checkOfflineStatus)
})

onUnmounted(() => {
    appEvents.off('schedule:refresh', fetchEvents)
    appEvents.off('offline:schedule-downloaded', checkOfflineStatus)
    appEvents.off('offline:schedule-cleared', checkOfflineStatus)
    window.removeEventListener('online', fetchEvents)
    window.removeEventListener('offline', checkOfflineStatus)
    eventDetailModal.value?.close()
    eventFormModal.value?.close()
    const backdrops = document.querySelectorAll('[modal-backdrop]')
    backdrops.forEach(b => b.remove())
    document.body.classList.remove('overflow-hidden')
})
</script>